﻿import React, { forwardRef, ReactNode, useEffect, useImperativeHandle } from 'react'
import { useValidatedState } from '../validation'
import { Button } from '../buttons'
import { useCommitedState } from '../blockers'
import { FormGridDetailsMehods, FormGridContext, FormGridDetailsContext } from './Types'

export interface FormGridDetailsProps<R extends {}> {
  id?: string;
  initial: () => R
  ctx: FormGridContext<R>
  children?: (ctx: FormGridDetailsContext<R>) => ReactNode
}

const FormGridDetailsInternal = <R extends {}>(props: FormGridDetailsProps<R>, ref: React.ForwardedRef<FormGridDetailsMehods<R>>) => {
  const [model, setModel, errors, validate] = useValidatedState<R>(()=>{
    if(props.ctx.row) {
      return {...props.ctx.row, invalid: false};
    }
    else {
      return props.initial();
    }
  }, props.ctx.schema);

  // Prepare container id and container selector for validation purposes
  const containerId = "form-grid-details"
  const containerSelector = `#${containerId}`

  // Implement exposed methods, specifically to return uncommited details
  useImperativeHandle(ref, () => ({
    getUncommited: async (revalidate: boolean) => {
      var isValid;
      if (revalidate) {
        isValid = await validate(containerSelector)
      }
      else {
        isValid = props.ctx.row ? !props.ctx.invalid : true;
      }
      return [props.ctx.row, model, !isValid]
    }
  }));

  // Revalidate data if they were marked invalid
  useEffect(() => {
    if (props.ctx.row && props.ctx.invalid) {
      validate(containerSelector);
    }
  }, [])

  // Configure commited state
  useCommitedState(model);

  const detailsCtx: FormGridDetailsContext<R> = {
    readOnly: props.ctx.readOnly,
    model, 
    setModel, 
    errors, 
    validate
  }

  return (
    <>
      <div id={containerId}>
        {props.children ? props.children(detailsCtx): <></>}
      </div>
      {!props.ctx.readOnly && (
        <div className="row mt-4">
          <div className="col">
            <Button
              id={props.id ? `add-or-update-grid-${props.id}`:'add-or-update-grid'}
              label={props.ctx.row ? "Update" : "Add"}
              onClick={() => validate(containerSelector).then(v => v && props.ctx.onChange(props.ctx.row, model))}
            />
            <Button
              id={props.id ? `cancel-grid-${props.id}`: 'cancel-grid'}
              className="ml-4"
              label={"Cancel"}
              onClick={() => props.ctx.onChange(props.ctx.row, props.ctx.row)}
            />
          </div>
        </div>
      )}
    </>
  );
};

const FormGridDetailsWithRef = (forwardRef(FormGridDetailsInternal) as any) as <R extends {}>(
  props: FormGridDetailsProps<R> & { ref: React.ForwardedRef<FormGridDetailsMehods<R>> }
) => ReturnType<typeof FormGridDetailsInternal>;

export const FormGridDetails = <R extends {}>(props: FormGridDetailsProps<R>) => {
  return <FormGridDetailsWithRef {...props} ref={props.ctx.detailsRef}/>
}